import { SystemAnimation } from '../System/systemAnimation';
import { clamp } from '../../Utils/math';
import { Catcher } from './catcher';

export enum ProgressEvent {
    Start,
    Stop,
    Progress,
}

export interface IProgressEventData {
    progress: number,
    remain: number,
}

/**
 * Detect progress action of the user
 */

export class ProgressCatcher extends Catcher<ProgressEvent, IProgressEventData> {
    /**
     * Current progress position to be catched
     */
    protected progressReal = 0;

    /**
     * Position of the progress catching the real progress due to animation
     */
    protected progressCatch = 0;

    /**
     * Progress left to be reached by progress animation
     */
    protected progressGap = 0;

    /**
     * Use to animate the catching
     * @param system System of the 3D scene
     * @param responsive If there is responsive changes, we may have to adapt progress height
     */
    constructor(system: SystemAnimation) {
        super(system, 'ProgressCatcher');
    }

    /**
     * Restart progress catcher
     */
    public restart(): void {
        this.progressReal = 0;
        this.progressCatch = 0;
        this.progressGap = 0;
        this._restart();
    }

    /**
     * @ignore
     */
    public start(): void {
        this._start();
        this.catch(this.progressReal, this.speed);
        this.notify(ProgressEvent.Progress, { progress: 0, remain: 0 });
        this.notify(ProgressEvent.Start, { progress: 0, remain: 0 });
    }

    /**
     * @ignore
     */
    public stop(): void {
        this._stop();
        this.notify(ProgressEvent.Stop, { progress: this.progressCatch, remain: 0 });
    }

    /**
    * Speed of the progress used when mousewheel or drag on phone
    */
    private speed = 0.02;

    /**
    * @ignore
    */
    private lastSpeed = 0.02;

    /**
    * Set the catch speed of the progressCatcher
    * @param speed The new speed
    */
    public setCatchSpeed(speed: number): void {
        this.speed = speed;
        this.lastSpeed = speed;
        if (this.animation.running) {
            this.catch(this.progressReal);
        }
    }

    private maximumCatchSpeed = 0.2;

    /**
     * Catch the progress
     * @param top Top position to be catched
     * @param speed At what speed should we catch the new position
     */
    public catch(progress: number, speed?: number, force?: boolean): void {
        if (!this.hasObservers()) return;

        let progressReal = (progress) || 0;
        if (progressReal === this.progressReal) return;
        progressReal = clamp(progressReal);

        // Sometimes on iphone, perc can go below 0
        this.progressReal = progressReal;

        let catchSpeed = (speed) || this.speed;
        // Bigger speed will make percentage go behind 100%
        // Alway keep a minimum inertia or scroll won't be fluide at all
        catchSpeed = Math.min(this.maximumCatchSpeed, catchSpeed);
        // * limit can't be just 0 or 1
        // Or it won't work with scroll which often send 0.999 value instead of 1
        const isLimit = (progressReal < 0.01 || progressReal > 0.99);
        const isRecent = (catchSpeed === this.lastSpeed && this.checkRecentCatch(100));
        if (!isLimit && isRecent && !force) return;
        this.lastSpeed = catchSpeed;

        this.progressStart = this.progressCatch;
        this.progressChange = progressReal - this.progressStart;
        let howmany = Math.round(5 / catchSpeed);
        howmany = Math.min(howmany, 500);
        this.animation.simple(howmany, (perc) => {
            this.progress(perc);
        });
    }

    private progressStart: number;

    private progressChange: number;

    public progress(perc: number): void {
        // We use catchSpeed to make perc is never equal to 0
        // This way the scroll start from the first mouseWheel
        this.progressCatch = this.progressStart + this.progressChange * perc;
        this.progressGap = this.progressReal - this.progressCatch;
        this.notifyProgress();
    }

    public setToProgress(progress: number): void {
        this.progressReal = progress;
        this.progressCatch = progress;
        this.progressGap = 0;
        this.notifyProgress();
    }

    protected notifyProgress(): void {
        this.notify(
            ProgressEvent.Progress,
            { progress: this.progressCatch, remain: this.progressGap }
        );
    }
}
